import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Fingerprint, Search, Loader2, Download, Plus, X, Save, Upload,
  Trash2, AlertTriangle, Edit3, RefreshCw, ListChecks,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API = process.env.REACT_APP_BACKEND_URL + '/api/attendance/biometric';
const API_ROOT = process.env.REACT_APP_BACKEND_URL + '/api';

const BiometricMapping = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [devices, setDevices] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState('');
  const [rows, setRows] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null); // mapping object or null
  const [saving, setSaving] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState('update'); // mappings default to update — re-running matches the existing flow
  const [form, setForm] = useState({ device_user_id: '', employee_code: '', device_id: '' });
  const [settings, setSettings] = useState({ cutoff_date: '2026-04-21', auto_process_on_push: true });
  // Stage 73.20m — one-click "why isn't my device syncing" diagnostic.
  const [diagnostic, setDiagnostic] = useState(null);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);

  const runDiagnostic = async () => {
    setDiagnosticLoading(true);
    try {
      const url = `${API}/sync-diagnostic` + (selectedCompany ? `?company_id=${selectedCompany}` : '');
      const { data } = await axios.get(url, { withCredentials: true });
      setDiagnostic(data);
    } catch (e) {
      toast.error('Diagnostic failed: ' + (e?.response?.data?.detail || e.message));
    }
    setDiagnosticLoading(false);
  };

  // Stage 73.20n — one-click download of unmapped device IDs as a
  // fill-and-reupload CSV. Saves the admin from clicking N times to map
  // each unmapped device user.
  const downloadUnmappedCsv = async () => {
    try {
      const url = `${API}/mapping/unmapped-export` + (selectedCompany ? `?company_id=${selectedCompany}` : '');
      const res = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'unmapped_device_ids.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Downloaded. Fill the employee_code column in Excel and re-upload via CSV Template → Bulk Upload.');
    } catch (e) {
      toast.error('Download failed: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_ROOT}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);
  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { withCredentials: true });
      setSettings(data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    fetchCompanies();
    fetchSettings();
  }, [fetchCompanies, fetchSettings]);
  const fetchDevices = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_ROOT}/attendance/devices?company_id=${selectedCompany}`, { withCredentials: true });
      setDevices(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);
  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('company_id', selectedCompany);
      if (selectedDevice) params.append('device_id', selectedDevice);
      if (search) params.append('search', search);
      const { data } = await axios.get(`${API}/mapping?${params}`, { withCredentials: true });
      setRows(data);
    } catch (e) { console.error(e); setRows([]); }
    finally { setLoading(false); }
  }, [search, selectedCompany, selectedDevice]);
  const fetchUnmapped = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('company_id', selectedCompany);
      const { data } = await axios.get(`${API}/unmapped?${params}`, { withCredentials: true });
      setUnmapped(data);
    } catch (e) { setUnmapped([]); }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchDevices();
      fetchMappings();
      fetchUnmapped();
    }
  }, [selectedCompany, selectedDevice, fetchDevices, fetchMappings, fetchUnmapped]);




  const saveSettings = async (next) => {
    try {
      await axios.patch(`${API}/settings`, next, { withCredentials: true });
      setSettings({ ...settings, ...next });
      toast.success('Settings saved');
    } catch (e) { toast.error('Failed to save settings'); }
  };



  const handleSave = async () => {
    if (!form.device_user_id || !form.employee_code) {
      toast.error('Device User ID and Employee Code are required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.patch(`${API}/mapping/${editing.id}`, form, { withCredentials: true });
        toast.success('Mapping updated');
      } else {
        await axios.post(`${API}/mapping`, form, { withCredentials: true });
        toast.success('Mapping created');
      }
      setShowAdd(false);
      setEditing(null);
      setForm({ device_user_id: '', employee_code: '', device_id: '' });
      fetchMappings();
      fetchUnmapped();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this mapping?')) return;
    try {
      await axios.delete(`${API}/mapping/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchMappings();
    } catch (e) { toast.error('Delete failed'); }
  };

  const handleBulkUpload = async (dryRun = false) => {
    if (!bulkFile) { toast.error('Pick a CSV first'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const params = new URLSearchParams();
      if (selectedCompany) params.append('company_id', selectedCompany);
      params.append('dry_run', dryRun);
      params.append('duplicate_mode', duplicateMode);
      const { data } = await axios.post(
        `${API}/mapping/bulk-upload?${params}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setBulkResult(data);
      if (data.rejected_whole_file) {
        toast.error('File rejected — see details');
      } else if (data.error_count === 0 && !dryRun) {
        toast.success(`${data.created_count || 0} created, ${data.updated_count || 0} updated, ${data.skipped_count || 0} skipped`);
        fetchMappings();
        fetchUnmapped();
      } else if (dryRun) {
        toast.success(`Validation: ${data.success_count} ok, ${data.error_count} errors`);
      } else {
        toast.error(`${data.success_count} ok, ${data.error_count} failed — see details below`);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); }
  };

  const downloadTemplate = () => {
    window.open(`${API}/mapping/template`, '_blank');
  };

  const handleProcess = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (selectedCompany) params.append('company_id', selectedCompany);
      const { data } = await axios.post(`${API}/process?${params}`, null, { withCredentials: true });
      toast.success(`Processed: ${data.resolved} resolved, ${data.skipped_no_mapping} unmapped, ${data.attendance_recalculated} attendance days recalculated`);
      fetchUnmapped();
    } catch (e) { toast.error('Process failed'); }
    finally { setSaving(false); }
  };

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="biometric-mapping-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
              <Fingerprint className="inline-block w-8 h-8 mr-2 mb-1" /> Device Employee Mapping
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              Bridge between the <span className="font-semibold text-slate-800">short numeric ID on the biometric device</span>{' '}
              (e.g. <code className="bg-slate-100 px-1 rounded">1</code>, <code className="bg-slate-100 px-1 rounded">2</code>) and your{' '}
              <span className="font-semibold text-slate-800">auto-generated payroll employee code</span>{' '}
              (e.g. <code className="bg-slate-100 px-1 rounded">HECS-CORP-2026-0001</code>).
              Without this mapping, daily punches can't be linked to the right person.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={runDiagnostic} disabled={diagnosticLoading} data-testid="sync-diagnostic-btn">
              {diagnosticLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
              Sync Diagnostic
            </Button>
            <Button variant="outline" onClick={downloadTemplate} data-testid="mapping-template-btn">
              <Download className="w-4 h-4 mr-2" /> CSV Template
            </Button>
            <Button onClick={() => { setEditing(null); setForm({ device_user_id: '', employee_code: '', device_id: selectedDevice || '' }); setShowAdd(true); }}
              className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="add-mapping-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Mapping
            </Button>
          </div>
        </div>

        {/* Stage 73.20m — Sync Diagnostic panel — shows the FIRST broken
            link in the biometric → attendance pipeline with a fix per stage. */}
        {diagnostic && (
          <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="sync-diagnostic-panel">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                <ListChecks className="w-4 h-4" /> Biometric Sync — Pipeline Diagnostic
                {diagnostic.healthy ? (
                  <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-emerald-100 text-emerald-700">All stages OK</span>
                ) : (
                  <span className="ml-1 px-2 py-0.5 rounded-full text-xs bg-rose-100 text-rose-700">Issues found</span>
                )}
              </h3>
              <button onClick={() => setDiagnostic(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Stage cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">1. Devices</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['1_devices_registered']?.count ?? 0} / {diagnostic.stages['1_devices_registered']?.active ?? 0} active</div>
              </div>
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">2. Cutoff</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['2_settings']?.cutoff_date || '—'}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Auto: {diagnostic.stages['2_settings']?.auto_process_on_push ? 'on' : 'off'}
                </div>
              </div>
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">3. Raw punches (30d)</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['3_raw_punches']?.last_30_days ?? 0}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  Unprocessed: {diagnostic.stages['3_raw_punches']?.unprocessed ?? 0}
                </div>
              </div>
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">4. Mappings</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['4_mapping']?.total_active_mappings ?? 0}</div>
                {(diagnostic.stages['4_mapping']?.unmapped_device_users ?? 0) > 0 && (
                  <>
                    <div className="text-[10px] text-rose-600 mt-0.5">
                      {diagnostic.stages['4_mapping']?.unmapped_device_users} unmapped
                    </div>
                    <button
                      onClick={downloadUnmappedCsv}
                      className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="download-unmapped-csv-btn"
                      title="Download unmapped device IDs as CSV → fill employee_code in Excel → re-upload via Bulk Upload"
                    >
                      <Download className="w-3 h-3" /> Download CSV
                    </button>
                  </>
                )}
              </div>
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">5. Canonical (30d)</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['5_canonical_punches_last_30d'] ?? 0}</div>
              </div>
              <div className="bg-slate-50 rounded p-2 border border-slate-200">
                <div className="text-slate-500 mb-0.5">6. Attendance (30d)</div>
                <div className="font-semibold text-slate-900">{diagnostic.stages['6_attendance_records_last_30d'] ?? 0}</div>
              </div>
            </div>

            {/* Issues + fixes */}
            <div className="space-y-2">
              {(diagnostic.issues || []).map((iss, idx) => (
                <div
                  key={idx}
                  className={
                    iss.severity === 'blocker'
                      ? 'bg-rose-50 border border-rose-200 rounded p-3 text-xs'
                      : iss.severity === 'warning'
                        ? 'bg-amber-50 border border-amber-200 rounded p-3 text-xs'
                        : 'bg-emerald-50 border border-emerald-200 rounded p-3 text-xs'
                  }
                  data-testid={`diag-issue-${idx}`}
                >
                  <div className="font-semibold flex items-center gap-2">
                    {iss.severity === 'blocker' ? '🛑' : iss.severity === 'warning' ? '⚠️' : '✓'}
                    <span>{iss.stage}</span>
                    <span className="text-slate-500 font-normal">— {iss.issue}</span>
                  </div>
                  <p className="mt-1 text-slate-700">
                    <b>Fix:</b> {iss.fix}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings strip */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Effective from</span>
            <input
              type="date"
              data-testid="cutoff-date-input"
              value={settings.cutoff_date || '2026-04-21'}
              onChange={(e) => setSettings({ ...settings, cutoff_date: e.target.value })}
              onBlur={() => saveSettings({ cutoff_date: settings.cutoff_date })}
              className="h-9 px-3 border border-slate-300 rounded-md text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              data-testid="auto-process-toggle"
              checked={!!settings.auto_process_on_push}
              onChange={(e) => saveSettings({ auto_process_on_push: e.target.checked })}
            />
            Auto-process on push
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" onClick={handleProcess} disabled={saving} data-testid="run-process-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Process Pending Punches
            </Button>
          </div>
        </div>

        {/* Unmapped quick-fill panel */}
        {unmapped.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="unmapped-panel">
            <div className="flex items-center gap-2 text-amber-800 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-semibold">
                {unmapped.length} biometric device ID(s) sending punches but not yet linked to a payroll code
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {unmapped.slice(0, 12).map((u, i) => (
                <button
                  key={i}
                  data-testid={`unmapped-chip-${i}`}
                  onClick={() => {
                    setEditing(null);
                    setForm({
                      device_user_id: u.device_user_id,
                      employee_code: u.employee_code_hint || '',
                      device_id: u.device_id || '',
                    });
                    setShowAdd(true);
                  }}
                  className="px-2.5 py-1 text-xs rounded-full bg-white border border-amber-300 text-amber-900 hover:bg-amber-100"
                >
                  ID {u.device_user_id} <span className="text-amber-500 ml-1">{u.punch_count}×</span>
                </button>
              ))}
              {unmapped.length > 12 && <span className="text-xs text-amber-700 self-center">+{unmapped.length - 12} more</span>}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex items-center gap-3 flex-wrap">
          <select value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} data-testid="company-select"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={selectedDevice} onChange={(e) => setSelectedDevice(e.target.value)} data-testid="device-filter"
            className="h-10 px-3 bg-white border border-slate-300 rounded-md text-sm">
            <option value="">All Devices</option>
            {devices.map((d) => <option key={d.id} value={d.id}>{`${d.device_name} (${d.device_serial})`}</option>)}
          </select>
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              data-testid="search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchMappings()}
              placeholder="Search device ID, payroll code or name..."
              className="w-full h-10 pl-10 pr-4 bg-white border border-slate-300 rounded-md text-sm"
            />
          </div>
          <Button variant="outline" onClick={fetchMappings} data-testid="apply-filter-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {/* Bulk-upload card */}
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks className="w-4 h-4 text-[#0052FF]" />
            <span className="text-sm font-semibold text-slate-800">Bulk Upload Mappings</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <input
              type="file"
              accept=".csv"
              data-testid="bulk-file-input"
              onChange={(e) => { setBulkFile(e.target.files?.[0] || null); setBulkResult(null); }}
              className="text-sm"
            />
            <select
              value={duplicateMode}
              onChange={(e) => setDuplicateMode(e.target.value)}
              data-testid="dup-mode-select"
              className="h-9 px-3 border border-slate-300 rounded-md text-sm bg-white"
            >
              <option value="reject">If exists: Reject upload</option>
              <option value="update">If exists: Update in place</option>
              <option value="skip">If exists: Skip row</option>
            </select>
            <Button variant="outline" disabled={!bulkFile || saving} onClick={() => handleBulkUpload(true)} data-testid="dry-run-btn">
              Validate Only
            </Button>
            <Button disabled={!bulkFile || saving} onClick={() => handleBulkUpload(false)} className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="bulk-upload-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import
            </Button>
          </div>
          {bulkResult && (
            <div className="mt-3 text-sm" data-testid="bulk-result">
              {bulkResult.rejected_whole_file ? (
                <p className="text-red-700"><strong>File rejected — nothing was saved.</strong></p>
              ) : (
                <p className="text-slate-700">
                  <span className="font-semibold text-emerald-700">{bulkResult.created_count || 0}</span> created,{' '}
                  <span className="font-semibold text-blue-700">{bulkResult.updated_count || 0}</span> updated,{' '}
                  <span className="font-semibold text-slate-500">{bulkResult.skipped_count || 0}</span> skipped,{' '}
                  <span className="font-semibold text-red-700">{bulkResult.error_count || 0}</span> errors
                  {bulkResult.dry_run && <span className="ml-2 text-amber-700">(dry-run — nothing saved)</span>}
                </p>
              )}
              {bulkResult.errors?.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-slate-500">View errors</summary>
                  <ul className="text-xs text-red-700 mt-1 space-y-0.5">
                    {bulkResult.errors.slice(0, 50).map((e, i) => (
                      <li key={i}>Row {e.row}: {e.error}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {/* Mapping table */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="mapping-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">
                  Biometric Device ID
                  <span className="block normal-case text-[10px] text-slate-400 font-normal">short numeric, from device</span>
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">
                  Payroll Employee Code
                  <span className="block normal-case text-[10px] text-slate-400 font-normal">auto-generated by software</span>
                </th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Employee Name</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Device</th>
                <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Status</th>
                <th className="text-right py-3 px-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-slate-500">
                  <Fingerprint className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No mappings yet. Click "Add Mapping" or use the CSV upload above.</p>
                </td></tr>
              ) : rows.map((r, i) => {
                const dev = devices.find((d) => d.id === r.device_id);
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`mapping-row-${i}`}>
                    <td className="py-3 px-4 text-sm font-mono text-slate-900">{r.device_user_id}</td>
                    <td className="py-3 px-4 text-sm font-mono font-medium text-slate-900">{r.employee_code}</td>
                    <td className="py-3 px-4 text-sm text-slate-700">{r.employee_name}</td>
                    <td className="py-3 px-4 text-sm text-slate-600">{dev ? dev.device_name : (r.device_id ? r.device_id.slice(0, 8) : 'All devices')}</td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.is_active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                        {r.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <button
                        onClick={() => { setEditing(r); setForm({ device_user_id: r.device_user_id, employee_code: r.employee_code, device_id: r.device_id || '' }); setShowAdd(true); }}
                        data-testid={`edit-${i}`}
                        className="p-1.5 text-slate-500 hover:text-[#0052FF]"
                      >
                        <Edit3 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(r.id)}
                        data-testid={`delete-${i}`}
                        className="p-1.5 text-slate-500 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add/Edit modal */}
        {showAdd && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="mapping-modal">
            <div className="bg-white rounded-lg w-full max-w-md flex flex-col max-h-[90vh]">
              <div className="flex items-center justify-between p-5 border-b flex-shrink-0">
                <h2 className="text-lg font-semibold">{editing ? 'Edit Mapping' : 'Add Mapping'}</h2>
                <button onClick={() => { setShowAdd(false); setEditing(null); }} className="text-slate-400 hover:text-slate-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="p-5 space-y-4 overflow-y-auto flex-1">
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900">
                  These are <strong>two different IDs</strong>. The biometric device stores a short numeric ID
                  (1, 2, 3…). The payroll software auto-generates a long code like
                  <code className="ml-1 bg-white border border-blue-200 px-1 rounded font-mono text-[10px]">HECS-CORP-2026-0001</code>.
                  Both are required so we can link a punch on the device to the right employee.
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Biometric Device ID *</label>
                  <input
                    data-testid="device-user-id-input"
                    value={form.device_user_id}
                    onChange={(e) => setForm({ ...form, device_user_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="e.g. 1, 17, 102"
                  />
                  <p className="text-xs text-slate-500 mt-1">The short numeric ID stored on the eSSL/ZKTeco device.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Payroll Employee Code *</label>
                  <input
                    data-testid="employee-code-input"
                    value={form.employee_code}
                    onChange={(e) => setForm({ ...form, employee_code: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm font-mono"
                    placeholder="e.g. HECS-CORP-2026-0001"
                  />
                  <p className="text-xs text-slate-500 mt-1">The auto-generated code from the payroll software.</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">Device (optional)</label>
                  <select
                    data-testid="modal-device-select"
                    value={form.device_id}
                    onChange={(e) => setForm({ ...form, device_id: e.target.value })}
                    className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="">All devices (any device with this user-id)</option>
                    {devices.map((d) => <option key={d.id} value={d.id}>{`${d.device_name} (${d.device_serial})`}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex justify-end gap-2 p-5 border-t flex-shrink-0">
                <Button variant="outline" onClick={() => { setShowAdd(false); setEditing(null); }}>Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-[#0052FF] hover:bg-[#0043D1] text-white" data-testid="save-mapping-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {editing ? 'Save changes' : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default BiometricMapping;
